import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {DatePipe} from '@angular/common';
import {filter, map, tap, withLatestFrom} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import { AttributeGroupsFacadeService } from '@emm/management/services/attribute-groups-facade.service';
import {FormBuilder, FormGroup} from '@angular/forms';
import {AttributeGroup} from '@emm/core/models/attribute-group.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
  selector: 'emm-management-attribute-groups-form',
  templateUrl: './management-attribute-groups-form.component.html',
  styleUrls: ['./management-attribute-groups-form.component.scss']
})
export class ManagementAttributeGroupsFormComponent implements OnInit {

    isReadOnly$ = this.authFacade.isReadOnly$;
    attributeGroupDetail$ = this.attributeGroupsFacade.attributeGroupDetail$;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    form: FormGroup;
    model = {};
    pipe = new DatePipe('en-US');
    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    canEdit: boolean;

    model$ = this.attributeGroupDetail$.pipe(
        filter(RA.isNotNil),
        map(({ name, description }) => (this.form = this.formBuilder.group({
            name: [{value: name, disabled: !this.canEdit}],
            description: [{value: description, disabled: !this.canEdit}],
        }))),
        tap((model) => (this.model = model))
    );

    constructor(private attributeGroupsFacade: AttributeGroupsFacadeService,
                private authFacade: AuthFacadeService,
                private notificationService: NotificationService,
                private managementApiService: ManagementApiService,
                private formBuilder: FormBuilder,) {}



    ngOnInit(): void {
        this.canEdit = this.isSuperAdmin || this.isAdmin;
        this.model$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }


    cancel(): void {
        this.attributeGroupDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (attributeGroupDetail: AttributeGroup) => this.model = attributeGroupDetail);
        this.form.reset(this.model);
    }


    submit(): void {
        if (!this.hasChanges()) {
            this.notificationService.error('Sorry, there was an error. No updates were made.');
            return;
        }
        const updateAttributeGroup = {
            id: this.attributeGroupDetail$.getValue().id,
            name: this.form.controls['name'].value,
            description: this.form.controls['description'].value,
        } as AttributeGroup;
        this.managementApiService.updateAttributeGroup(updateAttributeGroup.id, updateAttributeGroup)
            .pipe(withLatestFrom(this.attributeGroupDetail$))
            .subscribe({
                next: (success) => {
                    if (success) {
                        this.notificationService.success('Saved : ' + updateAttributeGroup.id);
                        return;
                    }
                    this.notificationService.error('Could not save : ' + updateAttributeGroup.id);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
        this.attributeGroupsFacade.updateAttributeGroup(updateAttributeGroup)
    }


    hasChanges(): boolean {
        return this.attributeGroupDetail$.getValue().description !== this.form.controls['description'].value;
    }


}
